import { SideSheet, Tag, Typography, Button } from "@douyinfe/semi-ui";
import { IconPlay } from "@douyinfe/semi-icons";
import { Col, ColType, Crud } from "../../../comp/crud";
import { displayLeadStatus } from "../../../api/ApiClient";
import { intentionLevelLabel, intentionLevelStyle } from "../../../api/aiCoach";
import { displayStar } from "../../../utils";
import React, { useState } from "react";
import AudioPlayerModal from "../../../comp/AudioPlayerModal";

const { Text } = Typography;

export type DrawerMode = "customer" | "call" | "assign" | "visit";

export interface DetailDrawerProps {
  visible: boolean;
  title: string;
  /** 默认 customer；assign 复用客户列表但强制 entryMode=assigned */
  mode?: DrawerMode;
  params: Record<string, any>;
  onClose: () => void;
}

const openLeadDetail = (id: number) => {
  const w = window.open("about:blank");
  if (w) {
    const data = { id, ids: [] };
    w.location.href = "/#/leadDetailNew?data=" + encodeURIComponent(JSON.stringify(data));
  }
};

const openLeadProfile = (id: number) => {
  window.open(`/#/leadProfile?id=${id}`);
};

const callStatusMap: Record<string, { label: string; color: string }> = {
  CALLING: { label: "拨号中", color: "blue" },
  CONNECTED: { label: "已接通", color: "green" },
  ENDED: { label: "已接通", color: "green" },
  MISSED: { label: "未接通", color: "red" },
};

const TAG_FALLBACK_COLOR = "grey" as any;

function formatSeconds(s: number | null | undefined): string {
  if (s === null || s === undefined || s < 0) return "-";
  const min = Math.floor(s / 60);
  const sec = s % 60;
  return `${min}:${sec.toString().padStart(2, "0")}`;
}

const ellipsis = (maxWidth: number): React.CSSProperties => ({
  display: "inline-block",
  maxWidth,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  verticalAlign: "bottom",
});

const customerCols: Col[] = [
  { name: "客户编号", key: "leadNo", type: ColType.STRING, width: 110 },
  {
    name: "客户",
    key: "name",
    type: ColType.STRING,
    width: 140,
    render: (_: any, r: any) => (
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Text link strong onClick={() => openLeadDetail(r.id)}>
          {r.name || `#${r.id}`}
        </Text>
        <Text type="tertiary" size="small">ID: {r.id}</Text>
      </div>
    ),
  },
  { name: "手机", key: "fullMobile", type: ColType.STRING, width: 120 },
  {
    name: "状态",
    key: "status",
    type: ColType.STRING,
    width: 90,
    render: (s?: string) => (s ? <Tag color="blue">{displayLeadStatus(s)}</Tag> : <Text type="tertiary">-</Text>),
  },
  {
    name: "星级",
    key: "star",
    type: ColType.NUMBER,
    width: 70,
    render: (v?: number) => (v != null ? displayStar(v) : <Text type="tertiary">-</Text>),
  },
  {
    name: "意向",
    key: "intentionLevel",
    type: ColType.STRING,
    width: 90,
    render: (lvl?: string) =>
      lvl ? (
        <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span style={intentionLevelStyle(lvl)}>{lvl}</span>
          <Text type="tertiary" size="small">{intentionLevelLabel(lvl)}</Text>
        </div>
      ) : (
        <Text type="tertiary">-</Text>
      ),
  },
  {
    name: "资质",
    key: "remark",
    type: ColType.STRING,
    width: 220,
    render: (v: string) => <Text style={ellipsis(200)}>{v || "-"}</Text>,
  },
  { name: "分配时间", key: "assignedAt", type: ColType.DATETIME, width: 140 },
  { name: "最近跟进", key: "lastContactAt", type: ColType.DATETIME, width: 140 },
  {
    name: "跟进记录",
    key: "contactLogs",
    type: ColType.STRING,
    width: 240,
    render: (v: string) => <Text style={ellipsis(220)}>{v || "-"}</Text>,
  },
  {
    name: "操作",
    key: "__action__",
    type: ColType.STRING,
    width: 80,
    render: (_: any, r: any) => (
      <Button size="small" theme="borderless" onClick={() => openLeadProfile(r.id)}>
        画像
      </Button>
    ),
  },
];

const getCallCols = (playAudio: (url: string, title: string) => void): Col[] => [
  {
    name: "客户",
    key: "leadName",
    type: ColType.STRING,
    width: 120,
    render: (v: string, r: any) =>
      r.leadId ? (
        <Text link strong onClick={() => openLeadDetail(r.leadId)}>
          {v || `#${r.leadId}`}
        </Text>
      ) : (
        <Text>{v || "-"}</Text>
      ),
  },
  { name: "手机号", key: "leadMobile", type: ColType.STRING, width: 120 },
  { name: "销售", key: "saleName", type: ColType.STRING, width: 90 },
  {
    name: "通话状态",
    key: "status",
    type: ColType.SELECT,
    width: 100,
    render: (v: string) => {
      const info = callStatusMap[v] || { label: v, color: TAG_FALLBACK_COLOR };
      return (
        <Tag color={info.color as any} size="small">
          {info.label}
        </Tag>
      );
    },
    getOptions: () =>
      Promise.resolve([
        { label: "拨号中", value: "CALLING", key: "CALLING" },
        { label: "已接通", value: "ENDED", key: "ENDED" },
        { label: "未接通", value: "MISSED", key: "MISSED" },
      ]),
  },
  { name: "拨号时间", key: "callAt", type: ColType.STRING, width: 140 },
  {
    name: "通话时长",
    key: "callTimeLong",
    type: ColType.STRING,
    width: 90,
    render: (v: number) => formatSeconds(v),
  },
  { name: "录音文件", key: "recordName", type: ColType.STRING, width: 160 },
  {
    name: "录音",
    key: "recordUploaded",
    type: ColType.BOOL,
    width: 80,
    render: (v: boolean, r: any) =>
      v && r.recordFile ? (
        <Button
          theme="borderless"
          type="primary"
          icon={<IconPlay />}
          size="small"
          onClick={() => playAudio(r.recordFile, r.recordName || r.leadName || "录音")}
        >
          播放
        </Button>
      ) : (
        <Text type="tertiary">-</Text>
      ),
  },
  {
    name: "通话内容",
    key: "transcriptText",
    type: ColType.STRING,
    width: 240,
    render: (v: string) => <Text style={ellipsis(220)}>{v || "-"}</Text>,
  },
  {
    name: "创建时间",
    key: "createdAt",
    type: ColType.DATE_RANGE,
    width: 160,
    showInFilter: true,
  },
];

const visitCols: Col[] = [
  {
    name: "客户",
    key: "leadName",
    type: ColType.STRING,
    width: 130,
    render: (v: string, r: any) =>
      r.leadId ? (
        <Text link strong onClick={() => openLeadDetail(r.leadId)}>
          {v || `#${r.leadId}`}
        </Text>
      ) : (
        <Text>{v || "-"}</Text>
      ),
  },
  { name: "手机", key: "leadMobile", type: ColType.STRING, width: 120 },
  {
    name: "星级",
    key: "star",
    type: ColType.SELECT,
    width: 70,
    render: (v?: number) => (v != null ? displayStar(v) : <Text type="tertiary">-</Text>),
    getOptions: () =>
      Promise.resolve([
        { label: "0星", value: "0", key: "0" },
        { label: "1星", value: "1", key: "1" },
        { label: "2星", value: "2", key: "2" },
        { label: "3星", value: "3", key: "3" },
        { label: "4星", value: "4", key: "4" },
        { label: "5星", value: "5", key: "5" },
      ]),
  },
  { name: "城市", key: "city", type: ColType.STRING, width: 70 },
  { name: "销售", key: "saleName", type: ColType.STRING, width: 90 },
  { name: "当前销售", key: "currSaleName", type: ColType.STRING, width: 100 },
  {
    name: "资质",
    key: "remark",
    type: ColType.STRING,
    width: 220,
    render: (v: string) => <Text style={ellipsis(200)}>{v || "-"}</Text>,
  },
  {
    name: "登记时间",
    key: "createdAt",
    type: ColType.DATE_RANGE,
    width: 160,
    showInFilter: true,
  },
  { name: "确认时间", key: "confirmAt", type: ColType.DATETIME, width: 140 },
];

const MODE_CONFIG: Record<DrawerMode, { entityKey: string; cols: Col[]; name: string }> = {
  customer: { entityKey: "leads/listForDashboard", cols: customerCols, name: "客户明细" },
  assign: { entityKey: "leads/listForDashboard", cols: customerCols, name: "分配明细" },
  call: { entityKey: "leadCallLog", cols: [], name: "通话记录明细" },
  visit: { entityKey: "leadVisitSignLog", cols: visitCols, name: "上门登记明细" },
};

export default function DetailDrawer({ visible, title, mode = "customer", params, onClose }: DetailDrawerProps) {
  const [audioVisible, setAudioVisible] = useState(false);
  const [audioUrl, setAudioUrl] = useState("");
  const [audioTitle, setAudioTitle] = useState("");

  const playAudio = (url: string, t: string) => {
    setAudioUrl(url);
    setAudioTitle(t);
    setAudioVisible(true);
  };

  const cfg = mode === "call"
    ? { entityKey: "leadCallLog", cols: getCallCols(playAudio), name: "通话记录明细" }
    : MODE_CONFIG[mode];
  const finalParams = mode === "assign" ? { ...params, entryMode: "assigned" } : params;
  const drawerWidth = mode === "call" || mode === "visit" ? "80%" : "85%";

  return (
    <SideSheet
      title={
        <span style={{ display: "inline-block", maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", verticalAlign: "bottom" }}>
          {title}
        </span>
      }
      visible={visible}
      onCancel={onClose}
      width={drawerWidth}
      keepDOM
    >
      <Crud
        key={`${mode}:${title}:${JSON.stringify(finalParams)}`}
        name={cfg.name}
        entityKey={cfg.entityKey}
        pageSize={50}
        cols={cfg.cols}
        params={finalParams}
        hideTitle
        canExport={false}
      />
      <AudioPlayerModal
        visible={audioVisible}
        url={audioUrl}
        title={audioTitle}
        onClose={() => { setAudioVisible(false); setAudioUrl(""); }}
      />
    </SideSheet>
  );
}
